import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, Globe } from "lucide-react";

import i18n, { persistLanguage } from "../lib/i18n";
import { cn } from "../lib/cn";
import { isMarketingLang, isMarketingPath, translatePath } from "../marketing/content/routes";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const LANGUAGES = [
  { value: "fr", labelKey: "auth.language.fr", shortLabel: "FR" },
  { value: "en", labelKey: "auth.language.en", shortLabel: "EN" },
  { value: "es", labelKey: "auth.language.es", shortLabel: "ES" },
] as const;

interface LanguageSelectorProps {
  className?: string;
  compact?: boolean;
}

export function LanguageSelector({ className, compact = false }: LanguageSelectorProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const currentLang = i18n.language?.split("-")[0] || "fr";
  const currentShortLabel =
    LANGUAGES.find((option) => option.value === currentLang)?.shortLabel ?? "FR";

  const handleSelect = (lang: string) => {
    persistLanguage(lang);
    void i18n.changeLanguage(lang);
    document.documentElement.lang = lang;
    if (isMarketingLang(lang) && isMarketingPath(location.pathname)) {
      const next = translatePath(location.pathname, lang);
      if (next !== location.pathname) {
        navigate(next);
      }
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        className={cn(
          compact
            ? "inline-flex h-9 min-h-9 items-center gap-1.5 rounded-lg border border-input bg-card px-3 text-xs font-medium tracking-wide text-[#5C5A52] transition-colors hover:border-primary/25 hover:bg-[#FAFAF8]"
            : "inline-flex h-11 w-11 min-h-11 items-center justify-center rounded-chrome border border-input bg-card text-[#56544c] transition-colors hover:bg-[#FAFAF8]",
          className,
        )}
      >
        <Globe className="h-3.5 w-3.5 shrink-0" strokeWidth={1.8} aria-hidden />
        {compact ? <span>{currentShortLabel}</span> : null}
        <span className="sr-only">{t("auth.language.label")}</span>
      </PopoverTrigger>
      <PopoverContent align="end" className="min-w-[168px] border-input p-1.5 shadow-lg">
        {LANGUAGES.map((option) => {
          const isActive = currentLang === option.value;

          return (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option.value)}
              className={cn(
                "flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors",
                isActive
                  ? "bg-[#E6F1EC] font-semibold text-primary"
                  : "font-medium text-[#56544c] hover:bg-[#F5F5F5]",
              )}
            >
              <span>{t(option.labelKey)}</span>
              {isActive ? <Check className="h-4 w-4 shrink-0" strokeWidth={2.5} /> : null}
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
